import React, { useEffect, useRef, useState } from "react";
import "video.js/dist/video-js.css";
import style from "../style.module.scss";
import classnames from "classnames";
import "videojs-youtube/dist/Youtube.js";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { useTranslation } from "react-i18next";

import { getVideoId, Youtube, YouTubeProps } from "./Youtube";
import { YoutubeInfo } from "./YoutubeInfo";
import { CookieConsent } from "@jmc/solid-design-system/src/utils/cookieConsent";

/*
 * Preload the Youtube videos by displaying a placeholder with the image poster
 * instead of loading the iframe from the begining.
 * This will avoid performance problems in pages with a lot of videos, cause we won't
 * load them until the user clicks on them.
 */
export const YoutubePreload: React.FunctionComponent<YouTubeProps> = (props: any) => {
    const [clicked, setClicked] = useState(false);
    const [accepted, setAccepted] = useState("loading");
    const [largeThumbnail, setLargeThumbnail] = useState(false);
    const { t } = useTranslation();

    const {
        showInHeader = false,
        thumbnail,
        videoId,
        className = null,
        isAudio = false,
        showLargePlayButton = true,
        warningMessage = undefined,
        cookieAccept = null,
        contentAlignment = "center",
        contentVerticalAlignment = "center",
        play = false,
        autoplay = false,
    } = props;

    const ref = useRef<HTMLDivElement>(null);

    const buildThumbnailUrl = (thumbnail: string): string => {
        const refWidth = ref?.current?.getBoundingClientRect()?.width;
        let width = 640;

        // Use larger thumbnail if it was already loaded
        if (refWidth > 640 || largeThumbnail) {
            width = 1920;
            if (!largeThumbnail) {
                setLargeThumbnail(true);
            }
        }

        let videoThumbnail = "";

        if (thumbnail) {
            if (thumbnail?.startsWith("http")) {
                // Thumbnail comes from CS
                const url = new URL(thumbnail);
                url.searchParams.append("width", width);
                url.searchParams.append("disable", "upscale");
                videoThumbnail = url.href;
            } else {
                // Thumbnail is a local path
                videoThumbnail = thumbnail;
            }
        } else {
            // Get Youtube thumbnail
            // See: https://stackoverflow.com/questions/2068344/how-do-i-get-a-youtube-video-thumbnail-from-the-youtube-api
            videoThumbnail = `https://img.youtube.com/vi/${getVideoId(videoId)}/sddefault.jpg`;
        }
        return videoThumbnail;
    };

    const videoPoster = buildThumbnailUrl(thumbnail);

    const clickHandler = () => {
        setClicked(true);
    };

    useEffect(() => {
        const scriptStatus = (): void => {
            const activeGroups = (window as any)?.OptanonActiveGroups as string;
            const currentStatus =
                activeGroups?.split(",")?.includes(`${CookieConsent.FUNTIONAL_COOKIES}`) || activeGroups === undefined
                    ? "ready"
                    : "blocked";
            setAccepted(currentStatus);
            cookieAccept && cookieAccept(currentStatus === "ready");
        };

        scriptStatus();
        window.addEventListener("OptanonActiveGroupsChanged", scriptStatus);
        return () => {
            window.removeEventListener("OptanonActiveGroupsChanged", scriptStatus);
        };
    });

    const component =
        clicked || play || autoplay ? (
            <Youtube {...props} startPlaying={true} thumbnail={videoPoster} />
        ) : (
            <div
                ref={ref}
                onClick={() => clickHandler()}
                className={classnames(
                    className,
                    style.player,
                    !isAudio || thumbnail ? style.videoPlayer : style.ajs,
                    !showLargePlayButton || (!thumbnail && isAudio) ? style.hidePlay : style.playButton,
                    accepted === "blocked" && style.blocked,
                )}
                onKeyDown={(e: KeyboardEvent): void => keyHandler(e.key, () => clickHandler())}
                data-test-id={`Video.YouTube.Preload.${getVideoId(videoId)}`}
                role="button"
                tabIndex={0}
            >
                {accepted === "blocked" && warningMessage && (
                    <div
                        id={`cookie-warning-${getVideoId(videoId)}`}
                        className={style.warning}
                        data-test-id={`VideoPlayer.${videoId}`}
                    >
                        {warningMessage}
                    </div>
                )}
                {accepted !== "blocked" && (
                    <div
                        className={
                            "vjs-big-play-centered vjs-16-9 video-js vjs-paused vjs-controls-enabled vjs-workinghover vjs-v7 vjs-user-active vjs-youtube vjs_video_7310-dimensions vjs-waiting"
                        }
                    >
                        <div
                            className="vjs-poster"
                            style={{ backgroundImage: `url(${videoPoster})`, backgroundSize: "cover" }}
                        />

                        <div className={style.playButton}>
                            <button
                                className={"vjs-big-play-button"}
                                type="button"
                                title={t("Play", { ns: "common" })}
                                aria-disabled="false"
                            >
                                <span className="vjs-icon-placeholder" aria-hidden="true"></span>
                                <span className="vjs-control-text" aria-live="polite">
                                    {t("Play", { ns: "common" })}
                                </span>
                            </button>
                        </div>
                    </div>
                )}
                <YoutubeInfo
                    showInHeader={showInHeader}
                    isAudio={isAudio}
                    thumbnail={thumbnail}
                    contentAlignment={contentAlignment}
                    contentVerticalAlignment={contentVerticalAlignment}
                    showLargePlayButton={showLargePlayButton}
                />
            </div>
        );

    return component;
};

export default YoutubePreload;
